/**
 * Leitura de documento (pedido do Vitor, 13/10): o regulamento aberto pelo link do checkout, com a mesma cara de
 * pagina de texto da politica de privacidade do site. `PaginaDeDocumento` e a folha (marca, titulo, versao e acoes);
 * `TextoDeDocumento` transforma o texto puro publicado em titulos, itens, marcadores e quadros (`documento.ts`).
 */
import { Fragment, type ReactNode } from "react";
import { blocosDoTexto, trechosComLinks, type BlocoDeDocumento } from "../documento";
import { AlternarTema } from "../tema";
import { MarcaVisual, type Marca } from "./TelaDeAcesso";

function Linha({ texto }: { texto: string }) {
  return (
    <>
      {trechosComLinks(texto).map((t, i) =>
        t.href ? (
          <a key={i} href={t.href} target="_blank" rel="noreferrer">{t.texto}</a>
        ) : (
          <Fragment key={i}>{t.texto}</Fragment>
        ),
      )}
    </>
  );
}

/** "Importante:", "Exemplo:": frase curta que abre o que vem embaixo, em destaque. */
const ehChamada = (t: string) => t.length <= 40 && /:$/.test(t);

function Blocos({ blocos }: { blocos: BlocoDeDocumento[] }) {
  return (
    <>
      {blocos.map((b, i) => {
        switch (b.tipo) {
          case "secao":
            return <h2 key={i} className="mtc-documento__secao"><Linha texto={b.texto} /></h2>;
          case "subsecao":
            return <h3 key={i} className="mtc-documento__subsecao"><Linha texto={b.texto} /></h3>;
          case "tabela":
            return (
              <div key={i} className="mtc-documento__tabela" role="region" aria-label={b.cabecalho.join(", ")} tabIndex={0}>
                <table>
                  <thead><tr>{b.cabecalho.map((c, j) => <th key={j} scope="col">{c}</th>)}</tr></thead>
                  <tbody>{b.linhas.map((l, j) => <tr key={j}>{l.map((c, k) => <td key={k}><Linha texto={c} /></td>)}</tr>)}</tbody>
                </table>
              </div>
            );
          case "subtitulo":
            return <h3 key={i} className="mtc-documento__subtitulo"><Linha texto={b.texto} /></h3>;
          case "paragrafo":
            return ehChamada(b.texto) ? <p key={i} className="mtc-documento__chamada">{b.texto}</p> : <p key={i}><Linha texto={b.texto} /></p>;
          case "marcadores":
            return <ul key={i} className="mtc-documento__marcadores">{b.itens.map((x, j) => <li key={j}><Linha texto={x} /></li>)}</ul>;
          case "campos":
            // Uma linha so ("Última atualização: 16 de julho de 2026") e frase; duas ou mais sao o quadro do plano.
            if (b.linhas.length === 1 && b.linhas[0]!.valor) return <p key={i}><strong>{b.linhas[0]!.campo}:</strong> <Linha texto={b.linhas[0]!.valor} /></p>;
            return (
              <dl key={i} className="mtc-documento__quadro">
                {b.linhas.map((l, j) =>
                  l.valor ? (
                    <div key={j} className="mtc-documento__campo"><dt>{l.campo}</dt><dd><Linha texto={l.valor} /></dd></div>
                  ) : (
                    <div key={j} className="mtc-documento__campo mtc-documento__campo--nota"><dt>{l.campo}</dt></div>
                  ),
                )}
              </dl>
            );
          case "numerada":
            return (
              <ol key={i} className="mtc-documento__itens">
                {b.itens.map((it, j) => (
                  <li key={j} value={it.numero}>
                    <Linha texto={it.texto} />
                    {it.filhos.length > 0 && <div className="mtc-documento__dentro"><Blocos blocos={it.filhos} /></div>}
                  </li>
                ))}
              </ol>
            );
        }
      })}
    </>
  );
}

/** O texto do documento formatado. `titulo` tira a primeira linha quando ela so repete o titulo da pagina. */
export function TextoDeDocumento({ texto, titulo }: { texto: string; titulo?: string }) {
  return (
    <div className="mtc-documento">
      <Blocos blocos={blocosDoTexto(texto, { titulo })} />
    </div>
  );
}

/**
 * A folha de um documento publico: a marca e o tema no alto, o rotulo ("Regulamento"), o titulo, a linha da versao e
 * as acoes (abrir o original, imprimir). Clara e escura como o resto; na impressao, so o texto.
 */
export function PaginaDeDocumento({ marca, rotulo, titulo, meta, acoes, children }: { marca: Marca; rotulo: string; titulo: string; meta?: ReactNode; acoes?: ReactNode; children: ReactNode }) {
  return (
    <div className="mtc-folha">
      <header className="mtc-folha__topo">
        <div className="mtc-folha__marca"><MarcaVisual marca={marca} classe="mtc-folha__logo" /></div>
        <AlternarTema />
      </header>
      <main className="mtc-folha__papel">
        <div className="mtc-folha__cabeca">
          <span className="mtc-folha__rotulo">{rotulo}</span>
          <h1 className="mtc-folha__titulo">{titulo}</h1>
          {meta && <p className="mtc-folha__meta">{meta}</p>}
          {acoes && <div className="mtc-folha__acoes">{acoes}</div>}
        </div>
        {children}
      </main>
      <footer className="mtc-folha__rodape">{marca.nome}</footer>
    </div>
  );
}
